import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'antd';

import ContextBar from '../components/common/ContextBar';
import GroupHeader from '../components/common/GroupHeader';
import { ReorderButton } from '../components/common/SortableList';
import AddToHomeModal from '../components/home/AddToHomeModal';
import HomeHeading, { EditHomeTitleModal } from '../components/home/HomeHeading';
import WelcomeTourOffer from '../components/home/WelcomeTourOffer';
import HomeTransferOffers from '../components/home/HomeTransferOffers';
import PinnedCharts from '../components/home/HomeCharts';
import { PIN_ANGLE } from '../components/icons';
import { DATA_STATE, DataState, Skeleton, deriveDataState } from '../components/loading';
import {
	HomeCollectionGroups,
	HomeCollections,
	HomeMore,
	HomeRecent,
} from '../components/home/HomeSections';
import PinnedStrip from '../components/home/PinnedStrip';
import { useWelcomeTour } from '../contexts/WelcomeTourContext';
import useDocumentTitle from '../hooks/useDocumentTitle';
import useHome from '../hooks/useHome';
import useChartPinsRun, { HOME_CHART_BATCH } from '../hooks/useChartPinsRun';
import useResolvedViewHistory from '../hooks/useResolvedViewHistory';
import { fetchRecentViews, subscribeToViewChanges } from '../services/views';
import { formatCount } from '../utils/counts';
import { useOnSessionChange } from '../utils/session';

// How much of the history Home shows before handing over to /history.
const RECENT_LIMIT = 8;

export const HOME_EMPTY_TEXT = 'Nothing pinned yet — choose Add to Home to pin quick adds, cards, records and charts from any collection.';

/**
 * The signed-in Home (`/`): the things this person does most, from every
 * collection they use — not any one collection's home. Four sections, in the
 * order the work goes:
 *
 *   Pinned       ONE dense strip (KPR-1123, KPE-036) of every shortcut the
 *                person pinned — a quick add, a multi add, "add an item of
 *                this card here", a dashboard, a record, a tag page, a
 *                combined list — in the one order they set, each a compact
 *                cell one click from what it opens or launches (PinnedStrip):
 *                two across on a phone, more as the width allows, the first
 *                three rows and "Show all (n)" for the rest.
 *   Charts       charts pinned from any collection (charts A7, ruled q16 and
 *                d18), each small or wide, run in ONE batch after Home has
 *                painted — the first 12, "Show more" for the rest. Home is
 *                never a dashboard: these are the person's own pins. Charts
 *                draw rather than link, so they keep their own section.
 *   Recent       what they opened lately (the view history), then History.
 *   Collections  the personal collection, then the favorite ones — ONE
 *                order with the rail's Pinned and the collections page's
 *                Favorites (a reorder here moves all three) — then the
 *                person's private tags that sit on collections, each with
 *                its collections (ruled d12: a collection in every group it
 *                belongs to). Home is never a dashboard: these are the
 *                person's own groupings, and a collection they put no
 *                private tag on stays exactly where it was.
 *
 * Reordering is deliberate: each section that can move carries a Reorder
 * button in its header, and only then do its rows show grips (and their ⋯
 * Move up / Move down); Done puts them away. One section at a time.
 *
 * The bar's one primary, **Add to Home**, opens AddToHomeModal: every
 * collection, one at a time, and what each offers to pin — so nobody has to
 * hunt through collection homes and ⋯ menus for things to put here.
 *
 * Pins come from GET /api/home, already access-filtered: a pin the person
 * can no longer use is left out, and the page says how many.
 */
export default function HomePage() {
	const { home, loading, busy, error, reload, reorder, unpin, resize, collections: collectionPins, reorderCollections } = useHome();
	// The welcome tour's offer (KPR-613). Its progress was read at sign-in; the
	// page waits for that answer (bounded, see WelcomeTourContext) so the offer
	// is either in the first paint or not there at all, never popping in later.
	const { loading: tourProgressLoading } = useWelcomeTour();
	const [adding, setAdding] = useState(false);
	const [editingTitle, setEditingTitle] = useState(false);
	// The bar's title is the person's heading (avatar + greeting or own title),
	// so the browser tab is named here: it still says Home (KPR-1122).
	useDocumentTitle('Home');
	const editTitleAction = { key: 'edit-title', group: 'record', glyph: 'edit', label: 'Edit title', onClick: () => setEditingTitle(true) };
	// The section in reorder mode — 'pinned' | 'charts' | 'collections' — or null.
	const [reordering, setReordering] = useState(null);
	const toggleReorder = key => setReordering(current => (current === key ? null : key));

	// ---- Recent: the view history, re-read whenever it changes ----
	const [views, setViews] = useState(null);
	// The history read failed. Held apart from `views`, so a failure is never
	// drawn as "Nothing opened yet" and Retry can ask again on its own.
	const [viewsError, setViewsError] = useState(false);
	const requestRef = useRef(0);
	// Per account, like every cache (utils/session.js).
	const session = useOnSessionChange(() => {
		requestRef.current += 1;
		setViews(null);
		setViewsError(false);
	});
	const loadViews = useCallback(() => {
		const token = ++requestRef.current;
		fetchRecentViews(RECENT_LIMIT)
			.then(rows => { if (token === requestRef.current) { setViews(rows); setViewsError(false); } })
			.catch(() => { if (token === requestRef.current) setViewsError(true); });
	}, []);
	const retryViews = useCallback(() => {
		setViewsError(false);
		loadViews();
	}, [loadViews]);
	useEffect(loadViews, [loadViews, session]);
	useEffect(() => subscribeToViewChanges(loadViews), [loadViews]);
	const recentRows = useResolvedViewHistory(views);

	const pinned = home ? (home.pinned || []) : [];
	const charts = home ? (home.charts || []) : [];
	// How many charts are drawn and run (⚑ d13: the first 12, then more).
	const [chartCount, setChartCount] = useState(HOME_CHART_BATCH);
	const chartRun = useChartPinsRun(charts, { count: chartCount, enabled: Boolean(home) && reordering !== 'charts' });
	const collectionGroups = home ? (home.collectionGroups || []) : [];
	const personal = home ? home.personal : null;
	const noPins = Boolean(home) && !pinned.length && !charts.length;

	// Each part of Home answers for itself (§14): the pins and collections come
	// from one read and share its state; Recent is its own read. A failure shows
	// where it happened, with its own Retry, and the other parts keep what they have.
	const pinsState = deriveDataState({
		loading: loading || busy,
		error: error && !home ? error : null,
		hasData: Boolean(home),
	});
	// The collections strip waits with the pins but leaves the failing to them.
	const collectionsState = pinsState === DATA_STATE.ERROR ? DATA_STATE.READY : pinsState;
	const recentState = deriveDataState({
		loading: views === null && !viewsError,
		error: viewsError ? 'error' : null,
		hasData: views !== null,
	});
	const collectionRows = collectionPins.map(pin => ({ id: pin.target_id, collection: pin.collection }));

	// A section offers Reorder once it has two rows that can move — and keeps
	// Done while it is in the mode, whatever an unpin leaves behind.
	const reorderAction = (key, movable, what) => (movable >= 2 || reordering === key ? (
		<ReorderButton active={reordering === key} onClick={() => toggleReorder(key)} what={what} />
	) : null);

	// Waiting on the tour progress read (made at sign-in, so this is rarely
	// seen): the page holds one skeleton instead of drawing and then moving
	// down when the offer arrives.
	if (tourProgressLoading) {
		return (
			<div className="home-page">
				<ContextBar title={<HomeHeading />} nameTab={false} />
				<DataState state={DATA_STATE.LOADING} skeleton={<Skeleton variant="list-row" count={3} />} label="your pins" className="home-page__state" />
			</div>
		);
	}

	return (
		<div className="home-page">
			<ContextBar
				title={<HomeHeading />}
				nameTab={false}
				menuActions={home === false ? null : [editTitleAction]}
				actions={home === false ? null : (
					<Button type="primary" icon={<PIN_ANGLE />} onClick={() => setAdding(true)}>
						Add to Home
					</Button>
				)}
			/>
			{adding ? <AddToHomeModal open={adding} onClose={() => setAdding(false)} /> : null}
			{editingTitle ? <EditHomeTitleModal open={editingTitle} onClose={() => setEditingTitle(false)} /> : null}

			<WelcomeTourOffer />

			{/* A collection someone offers this person waits here for an answer (KPR-743). */}
			<HomeTransferOffers />

			{home?.dropped > 0 ? (
				<p className="home-page__dropped meta-line" role="note">
					{formatCount(home.dropped, 'pin')} {home.dropped === 1 ? 'is' : 'are'} hidden because you can no longer use {home.dropped === 1 ? 'it' : 'them'}.
				</p>
			) : null}

			<DataState
				state={pinsState}
				hasData={Boolean(home)}
				skeleton={<Skeleton variant="list-row" count={3} />}
				error={error ? 'Try again in a moment.' : null}
				onRetry={reload}
				label="your pins"
				className="home-page__state"
			>
				{noPins ? (
					<p className="home-page__empty">{HOME_EMPTY_TEXT}</p>
				) : null}

				{pinned.length ? (
					<section className="home-page__section" aria-label="Pinned">
						<GroupHeader
							label="Pinned"
							count={pinned.length}
							action={reorderAction('pinned', pinned.length, 'pinned shortcuts')}
						/>
						<PinnedStrip
							pins={pinned}
							reordering={reordering === 'pinned'}
							onReorder={ids => reorder('pinned', ids)}
							onUnpin={unpin}
						/>
					</section>
				) : null}

				{charts.length ? (
					<section className="home-page__section" aria-label="Charts">
						<GroupHeader
							label="Charts"
							count={charts.length}
							action={reorderAction('charts', charts.length, 'charts')}
						/>
						<PinnedCharts
							pins={charts}
							results={chartRun.results}
							loading={chartRun.loading}
							onRetry={chartRun.refresh}
							count={chartCount}
							moreStep={HOME_CHART_BATCH}
							onShowMore={() => setChartCount(n => n + HOME_CHART_BATCH)}
							reordering={reordering === 'charts'}
							onReorder={ids => reorder('charts', ids)}
							onUnpin={unpin}
							onResize={resize}
						/>
					</section>
				) : null}

			</DataState>

			<section className="home-page__section" aria-label="Recent">
				<GroupHeader label="Recent" action={<HomeMore to="/history">View history</HomeMore>} />
				<DataState
					state={recentState}
					hasData={views !== null}
					skeleton={<Skeleton variant="list-row" count={2} />}
					error={viewsError ? 'Try again in a moment.' : null}
					onRetry={retryViews}
					label="recent records"
				>
					{recentRows.length ? (
						<HomeRecent rows={recentRows} />
					) : (
						<p className="home-page__quiet">Nothing opened yet — what you open shows up here.</p>
					)}
				</DataState>
			</section>

			{collectionsState === DATA_STATE.LOADING ? (
				<section className="home-page__section" aria-label="Collections">
					<GroupHeader label="Collections" />
					<DataState state={collectionsState} skeleton={<Skeleton variant="list-row" count={3} />} label="collections" />
				</section>
			) : null}

			{personal || collectionRows.length || collectionGroups.length ? (
				<section className="home-page__section" aria-label="Collections">
					<GroupHeader
						label="Collections"
						action={(
							<span className="home-page__header-actions">
								{reorderAction('collections', collectionRows.length, 'favorite collections')}
								<HomeMore to="/collections">All collections</HomeMore>
							</span>
						)}
					/>
					{personal || collectionRows.length ? (
						<HomeCollections
							personal={personal}
							collections={collectionRows}
							reordering={reordering === 'collections'}
							onReorder={reorderCollections}
						/>
					) : null}
					<HomeCollectionGroups groups={collectionGroups} />
				</section>
			) : null}
		</div>
	);
}
